import React, { useMemo, useState } from 'react';
import { Currency, formatMoney } from '../../../playground/common/format';
import { RequestResponseViewer } from '../../../playground/common/request-response-viewer';
import {
  ChartSeriesConfig,
  CurrencyHeader,
  EvolutionChart,
  KpiCard,
  KpiGrid,
  ProjectionColumn,
  ProjectionTable,
  SimulationWarnings,
  buildEvolutionSeries,
} from '../../../playground/endpoints/_shared';
import {
  UniversitySavingsV2MonthlyRow,
  UniversitySavingsV2Response,
  UniversitySavingsV2YearlyRow,
} from './service';

/**
 * `projection_by_month` rows carry no `calendar_month` for this product, and
 * the shared `month` column id renders a calendar name from it. The index is
 * republished under its own key so the monthly table shows a bare number.
 */
interface UniversitySavingsV2MonthlyTableRow {
  month_number: number;
  [key: string]: number | undefined;
}

function toMonthlyTableRow(
  row: UniversitySavingsV2MonthlyRow,
): UniversitySavingsV2MonthlyTableRow {
  return { ...row, month_number: row.month } as unknown as UniversitySavingsV2MonthlyTableRow;
}

const YEARLY_COLUMNS: ProjectionColumn<UniversitySavingsV2YearlyRow>[] = [
  { id: 'year', label: 'Año', isMoney: false },
  { id: 'age', label: 'Edad', isMoney: false },
  { id: 'actuarial_age', label: 'Edad actuarial', isMoney: false },
  { id: 'accumulated_prime', label: 'Prima acumulada', isMoney: true },
  { id: 'coverage_costs', label: 'Costo coberturas', isMoney: true },
  { id: 'policy_expenses', label: 'Gastos póliza', isMoney: true },
  { id: 'rentability', label: 'Rentado', isMoney: true },
  { id: 'policy_value', label: 'Valor póliza', isMoney: true },
  { id: 'withdrawal', label: 'Retiro universitario', isMoney: true },
  { id: 'policy_value_before_withdrawal', label: 'VP pre-retiro', isMoney: true },
  { id: 'policy_value_after_withdrawal', label: 'VP post-retiro', isMoney: true },
  { id: 'withdrawable_amount', label: 'Monto rescatable', isMoney: true },
  { id: 'death_compensation', label: 'Indemn. fallecimiento', isMoney: true },
];

const MONTHLY_COLUMNS: ProjectionColumn<UniversitySavingsV2MonthlyTableRow>[] = [
  { id: 'month_number', label: 'Mes', isMoney: false },
  { id: 'age', label: 'Edad', isMoney: false },
  { id: 'actuarial_age', label: 'Edad actuarial', isMoney: false },
  { id: 'accumulated_prime', label: 'Prima acumulada', isMoney: true },
  { id: 'coverage_costs', label: 'Costo coberturas', isMoney: true },
  { id: 'policy_expenses', label: 'Gastos póliza', isMoney: true },
  { id: 'policy_value', label: 'Valor póliza', isMoney: true },
  { id: 'rentability', label: 'Rentado', isMoney: true },
  { id: 'withdrawal', label: 'Retiro universitario', isMoney: true },
  { id: 'policy_value_before_withdrawal', label: 'VP pre-retiro', isMoney: true },
  { id: 'policy_value_after_withdrawal', label: 'VP post-retiro', isMoney: true },
  { id: 'withdrawable_amount', label: 'Monto rescatable', isMoney: true },
  { id: 'death_compensation', label: 'Indemn. fallecimiento', isMoney: true },
];

const CHART_SERIES: ChartSeriesConfig[] = [
  { dataKey: 'policy_value', name: 'Valor de la póliza', stroke: 'var(--bs-primary)' },
  { dataKey: 'accumulated_prime', name: 'Prima acumulada', stroke: 'var(--bs-gray-600)' },
  {
    dataKey: 'death_compensation',
    name: 'Indemn. fallecimiento',
    stroke: 'var(--bs-success)',
    strokeDasharray: '5 4',
  },
  { dataKey: 'withdrawal', name: 'Retiro universitario', stroke: 'var(--bs-warning)' },
];

const MONTHLY_CHART_SERIES: ChartSeriesConfig[] = [
  { dataKey: 'policy_value', name: 'Valor de la póliza', stroke: 'var(--bs-primary)' },
  { dataKey: 'withdrawal', name: 'Retiro', stroke: 'var(--bs-warning)' },
];

interface UniversitySavingsV2ResultViewProps {
  request: unknown;
  response?: UniversitySavingsV2Response;
  error?: unknown;
}

export function UniversitySavingsV2ResultView({
  request,
  response,
  error,
}: UniversitySavingsV2ResultViewProps) {
  const [currency, setCurrency] = useState<Currency>('UF');
  const hasSuccess = !!response && !error;

  const monthlyRows = useMemo(
    () => (response?.projection_by_month ?? []).map(toMonthlyTableRow),
    [response],
  );

  return (
    <div className="mt-4">
      {hasSuccess && response && (
        <>
          <CurrencyHeader
            ufValue={response.uf_value}
            currency={currency}
            onCurrencyChange={setCurrency}
          />

          <SimulationWarnings warnings={response.warnings} />

          <div className="mb-4">
            <KpiGrid>
              <KpiCard
                label="Ahorro bruto"
                value={formatMoney(response.savings.gross, currency, response.uf_value)}
                hint={`${response.timeframe_months} meses`}
                accent="text-primary"
              />
              <KpiCard
                label="Ahorro neto"
                value={formatMoney(response.savings.net, currency, response.uf_value)}
                accent="text-success"
              />
              <KpiCard
                label="Ganado por interés"
                value={formatMoney(response.savings.interest, currency, response.uf_value)}
              />
            </KpiGrid>
          </div>

          <div className="mb-4">
            <KpiGrid>
              <KpiCard
                label="Prima básica"
                value={formatMoney(response.premium.basic, currency, response.uf_value)}
              />
              <KpiCard
                label="Prima acordada"
                value={formatMoney(response.premium.agreed, currency, response.uf_value)}
              />
              <KpiCard
                label="Prima excedente"
                value={formatMoney(response.premium.excess, currency, response.uf_value)}
                accent="text-danger"
              />
            </KpiGrid>
          </div>

          <div className="mb-4">
            <h6 className="text-muted text-uppercase small">Financiamiento de aranceles</h6>
            <KpiGrid>
              <KpiCard
                label="Cobertura del ideal"
                value={`${(response.coverage_factor * 100).toFixed(1)}%`}
                hint="Arancel financiado sobre el arancel ideal"
                accent={response.coverage_factor < 1 ? 'text-warning' : 'text-success'}
              />
              <KpiCard
                label="Excedente terminal"
                value={formatMoney(response.terminal_excess, currency, response.uf_value)}
                hint="Saldo del valor póliza tras el último retiro"
                accent="text-info"
              />
              <KpiCard
                label="Mes del último retiro"
                value={String(response.objective_achieved_month)}
              />
            </KpiGrid>
          </div>

          <div className="mb-4">
            <h6 className="text-muted text-uppercase small">Beneficiarios</h6>
            <table className="table table-sm">
              <thead>
                <tr>
                  <th>Fecha de nacimiento</th>
                  <th>Inicio de estudios</th>
                  <th>Duración (años)</th>
                  <th>Arancel anual financiado</th>
                </tr>
              </thead>
              <tbody>
                {response.beneficiaries.map((beneficiary) => (
                  <tr key={beneficiary.date_of_birth}>
                    <td>{beneficiary.date_of_birth}</td>
                    <td>{beneficiary.start_date}</td>
                    <td>{beneficiary.duration_years}</td>
                    <td>{formatMoney(beneficiary.annual_tuition, currency, response.uf_value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mb-4">
            <EvolutionChart
              data={buildEvolutionSeries(
                response.projection_by_year,
                [
                  'policy_value',
                  'accumulated_prime',
                  'death_compensation',
                  'withdrawal',
                ],
                currency,
                response.uf_value,
              )}
              series={CHART_SERIES}
              currency={currency}
            />
          </div>

          {monthlyRows.length > 0 && (
            <>
              <div className="mb-4">
                <EvolutionChart
                  title="Evolución mensual de la póliza"
                  data={buildEvolutionSeries(
                    monthlyRows,
                    ['policy_value', 'withdrawal'],
                    currency,
                    response.uf_value,
                    ['month_number'],
                  )}
                  series={MONTHLY_CHART_SERIES}
                  currency={currency}
                  xKey="month_number"
                  xLabel="Mes"
                />
              </div>

              <div className="mb-4">
                <ProjectionTable
                  rows={monthlyRows}
                  columns={MONTHLY_COLUMNS}
                  currency={currency}
                  ufValue={response.uf_value}
                  title="Detalle mensual"
                  rowKey="month_number"
                />
              </div>
            </>
          )}

          <div className="mb-4">
            <ProjectionTable
              rows={response.projection_by_year}
              columns={YEARLY_COLUMNS}
              currency={currency}
              ufValue={response.uf_value}
            />
          </div>
        </>
      )}

      <RequestResponseViewer request={request} response={response} error={error} />
    </div>
  );
}
